import {
  type CanonicalStage,
  PROJECT_STAGE_DEFINITIONS,
  SIGNAL_TYPE_REGISTRY,
  buildLifecycleRail,
} from '@parcel/types';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger, cn } from '@parcel/ui';
import { ArrowUpRight } from 'lucide-react';
import type * as React from 'react';
import { normalizeExternalUrl } from '../../lib/external-url.js';
import {
  type Disposition,
  type Stage,
  dispositionMeta,
  dispositionRailStyle,
} from '../../lib/projects-display.js';
import { formatSignalCalendarDate } from '../../lib/signal-dates.js';
import type { ApiSignalType } from '../../lib/signals.js';

interface RailSignal {
  signal_type: ApiSignalType;
  filed_at: string;
  source_url: string | null;
}

interface LifecycleRailProps {
  stage: Stage | null;
  disposition: Disposition;
  signals: RailSignal[];
}

// Canonical one-sentence stage meanings, shared with the API and MCP.
const STAGE_DESCRIPTION = Object.fromEntries(
  PROJECT_STAGE_DEFINITIONS.map((d) => [d.value, d.description]),
) as Record<CanonicalStage, string>;

// The publisher's domain names the source link.
function sourceHost(href: string): string {
  try {
    return new URL(href).hostname.replace(/^www\./, '');
  } catch {
    return 'source';
  }
}

export function LifecycleRail({ stage, disposition, signals }: LifecycleRailProps) {
  // Stage -> the latest direct signal that set it (for the evidenced date + link).
  const signalByStage = new Map<CanonicalStage, RailSignal>();
  for (const signal of signals) {
    const rungStage = SIGNAL_TYPE_REGISTRY[signal.signal_type]?.stage;
    if (rungStage == null) continue;
    const existing = signalByStage.get(rungStage);
    if (!existing || new Date(signal.filed_at) > new Date(existing.filed_at)) {
      signalByStage.set(rungStage, signal);
    }
  }
  const evidencedStages = new Set<CanonicalStage>(signalByStage.keys());
  const rungs = buildLifecycleRail(stage, evidencedStages);
  const dispLabel = dispositionMeta(disposition).label;
  const railStyle = dispositionRailStyle(disposition);

  // Geometry: dots sit centered in equal-width columns, so the track is inset by
  // half a column on each side to run dot-center to dot-center.
  const n = rungs.length;
  const currentIndex = rungs.findIndex((r) => r.current);
  const inset = 100 / (2 * n);
  const reachedWidth = currentIndex >= 0 ? (currentIndex / n) * 100 : 0;
  const currentCenter = currentIndex >= 0 ? ((currentIndex + 0.5) / n) * 100 : 0;
  const tailWidth = currentIndex >= 0 ? ((n - 1 - currentIndex) / n) * 100 : 0;

  return (
    <TooltipProvider>
      <div className="relative">
        {/* Track behind the dots (vertically centered on the dot row). */}
        {currentIndex >= 0 ? (
          <>
            <div
              className="absolute rounded-full bg-brand"
              style={{ left: `${inset}%`, width: `${reachedWidth}%`, top: 9, height: 2 }}
              aria-hidden
            />
            {railStyle.tailDashed ? (
              <div
                className="absolute"
                style={{
                  left: `${currentCenter}%`,
                  width: `${tailWidth}%`,
                  top: 9,
                  borderTop: `2px dashed ${railStyle.tailColor}`,
                  opacity: 0.65,
                }}
                aria-hidden
              />
            ) : (
              <div
                className="absolute rounded-full bg-zinc-300 dark:bg-zinc-700"
                style={{ left: `${currentCenter}%`, width: `${tailWidth}%`, top: 9, height: 2 }}
                aria-hidden
              />
            )}
          </>
        ) : (
          <div
            className="absolute rounded-full bg-zinc-300 dark:bg-zinc-700"
            style={{ left: `${inset}%`, right: `${inset}%`, top: 9, height: 2 }}
            aria-hidden
          />
        )}

        {/* Dots + labels. */}
        <div className="relative z-[1] flex">
          {rungs.map((rung) => {
            const signal = signalByStage.get(rung.stage);
            const href = signal ? normalizeExternalUrl(signal.source_url) : undefined;

            let dot: React.ReactNode;
            if (rung.current) {
              dot = (
                <span
                  className="h-3.5 w-3.5 rounded-full bg-brand"
                  style={{ boxShadow: `0 0 0 5px ${railStyle.halo}` }}
                  aria-hidden
                />
              );
            } else if (rung.reached && rung.evidenced) {
              dot = <span className="h-3 w-3 rounded-full bg-brand" aria-hidden />;
            } else if (rung.reached) {
              dot = (
                <span
                  className="h-3 w-3 rounded-full border-2 border-brand bg-background"
                  aria-hidden
                />
              );
            } else {
              dot = (
                <span
                  className="h-3 w-3 rounded-full border-2 border-zinc-300 bg-background dark:border-zinc-600"
                  aria-hidden
                />
              );
            }

            const evidence = rung.evidenced ? signal : undefined;
            const title =
              rung.current && disposition !== 'active'
                ? `${rung.label} · ${dispLabel}`
                : rung.label;
            const detail = evidence
              ? formatSignalCalendarDate(evidence.filed_at)
              : rung.reached
                ? 'Implied'
                : 'Not reached';
            return (
              <Tooltip key={rung.stage}>
                <TooltipTrigger asChild>
                  <div className="flex min-w-0 flex-1 flex-col items-center gap-2">
                    <div className="flex h-5 items-center justify-center">{dot}</div>
                    <span
                      className={cn(
                        'truncate text-center text-[11px] leading-tight',
                        rung.current ? 'font-semibold text-foreground' : 'text-muted-foreground',
                      )}
                    >
                      {rung.label}
                    </span>
                  </div>
                </TooltipTrigger>
                <TooltipContent className="w-72 space-y-1.5 px-3 py-2.5">
                  <div className="flex items-baseline justify-between gap-3">
                    <span className="font-medium">{title}</span>
                    <span className="shrink-0 text-xs text-muted-foreground">{detail}</span>
                  </div>
                  <p className="text-xs leading-relaxed text-muted-foreground">
                    {STAGE_DESCRIPTION[rung.stage]}
                  </p>
                  {evidence && href ? (
                    <a
                      href={href}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 text-xs font-medium text-foreground hover:underline"
                    >
                      {sourceHost(href)}
                      <ArrowUpRight className="h-3 w-3" aria-hidden />
                    </a>
                  ) : null}
                </TooltipContent>
              </Tooltip>
            );
          })}
        </div>
      </div>
    </TooltipProvider>
  );
}
